import { createEnvelopeSource } from './create-envelope-source';

/**
 * Coupon-activation KPI source. Pure config over the standardized envelope
 * (`GET /analytics/coupons/activation`): `claimed` / `available` / `reissued`
 * are scalar counts, `redemptionRate` is the ratio. All mapping and fetching
 * lives in `createEnvelopeSource`. The line form requests `granularity=daily`
 * and renders the per-day series (the backend serves it: capabilities.daily=true);
 * the adapter only falls back to `{status:'pending'}` when no `series` is returned.
 *
 * `reissued` counts coupons reissued during the period, bounded on the reissue
 * date rather than the created date `claimed` uses. The endpoint scopes both
 * metrics through the same filter (promotion / age restriction / gamification),
 * so a filtered card narrows them identically; the date column is the only
 * difference between them. `reissued` is not a subset of `claimed`, and the two
 * must never be differenced.
 *
 * The `split` form shows available vs reclaimed for a single coupon: the card
 * carries a `promotionId` selection (see `useCouponList`), which the adapter
 * forwards as a query param so the endpoint returns that coupon's metrics
 * (without it the backend suppresses `available` / `redemptionRate`). The left
 * side reads the base `claimed` metric (rendered as "Redeemed"); the right side
 * reads `available` via an explicit `metricId` (rendered as "Total available"),
 * the coupon's lifetime catalog cap / allotment -- i.e. `Promotion.quantity`,
 * not a count of coupons emitted (period-independent, not province-attributable).
 * Redeemed counts activations within the selected period, so it can exceed the
 * total available over long windows.
 *
 * The backend suppresses `available` whenever the request carries a province
 * filter (the catalog cap is not province-attributable), so the Overview
 * "Coupon activated" card pins its spec's `region` to `[]` (see
 * `overview-page/cards.ts`), always querying without a province filter
 * regardless of the page Region filter. Any card reading `available`, whether
 * via `split`'s right side or the `columns` form, must pin the same
 * `region: []`; every other card here still forwards the page/spec region via
 * `regionGrain` below.
 */
export const couponActivationSource = createEnvelopeSource({
    label: 'Coupon activation',
    endpoint: 'analytics/coupons/activation',
    metrics: [
        { id: 'claimed', label: 'Coupons claimed', type: 'scalar' },
        { id: 'available', label: 'Coupons available', type: 'scalar' },
        { id: 'reissued', label: 'Coupons reissued', type: 'scalar' },
        { id: 'redemptionRate', label: 'Redemption rate', type: 'ratio' },
    ],
    forms: ['number', 'rate', 'line', 'split', 'columns'],
    split: {
        left: { label: 'Redeemed' },
        right: { label: 'Total available', metricId: 'available' },
    },
    columnsMetrics: [
        { label: 'available', metricId: 'available' },
        { label: 'reclaimed', metricId: 'claimed' },
    ],
    regionGrain: 'province',
});
